import { TCompaniesList, TUsersForComboType } from "../types/auth.types";
import {
  TGetAlllDahboardDataResponse,
  TGetAllQuestionsAndOptionsResponse,
  TQuestionsOptionsPreferencesRequest,
  TTherapistsJoinAnalysisRequest,
  TTherapistsJoinAnalysisResult,
  TTherapistsRolesAvailability,
} from "../types/user.types";
import {
  GetSalesDashboardParams,
  GetSalesDashboardResponse,
  IQuestionnaireData,
} from "../types/utils.types";
import {
  CrisisHotlineInsightsResponse,
  CrisisHotlineQuestionnairesResponse,
  CrisisHotlineQuestionsResponse,
  CrisisHotlineQuestionsWithOptionsResponse,
  CrisisHotlineQuestionAnalysisResponse,
  CrisisHotlineQuestionAnalysisParams,
} from "../types/crisisHotline.types";
import { api } from "./api";

export interface CrisisHotlineInsightsParams {
  start_date: string;
  end_date: string;
  therapistQuestionnaireIds?: number[];
  userQuestionnaireIds?: number[];
}

export interface GetTherapistsDashboardParams {
  start_date: string;
  end_date: string;
}

export interface TherapistDashboardTrend {
  value: number;
  isPositive: boolean;
  label: string;
}

export interface TherapistStatValue {
  value: number | string;
  trend?: TherapistDashboardTrend;
}

export interface TherapistDashboardStats {
  activeTherapists: TherapistStatValue;
  weeklyTreatments: TherapistStatValue;
  averageRating: TherapistStatValue;
  averageOccupancy: TherapistStatValue;
}

export interface TherapistData {
  name: string;
  specialization: string;
  appointments: number;
  occupancy: number;
  rating: string;
  income: string;
  status: string;
}

/**
 * Therapist performance chart row from GET /Dashboards/therapists.
 * `value` is the weekly average appointments (same as `averageWeeklyAppointments`).
 * `height` is an optional pre-normalized bar height from the API (e.g. "80%").
 */
export interface TherapistPerformanceData {
  name: string;
  /** Weekly average appointments; drives chart bar scale when `height` is omitted */
  value: number;
  averageWeeklyAppointments: number;
  averageMonthlyAppointments: number;
  averageQuarterlyAppointments: number;
  height?: string;
}

export interface GetTherapistsDashboardResponse {
  stats: TherapistDashboardStats;
  therapists: TherapistData[];
  performance: TherapistPerformanceData[];
}


const pickPerformanceField = (raw: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    const value = raw[key];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

/** Normalizes API performance rows (camelCase or PascalCase) for the therapists dashboard chart */
export const normalizeTherapistPerformanceItem = (
  raw: Record<string, unknown>
): TherapistPerformanceData => {
  const weekly =
    Number(
      pickPerformanceField(
        raw,
        'averageWeeklyAppointments',
        'AverageWeeklyAppointments',
        'value',
        'Value'
      )
    ) || 0;
  const monthly =
    Number(
      pickPerformanceField(raw, 'averageMonthlyAppointments', 'AverageMonthlyAppointments')
    ) || 0;
  const quarterly =
    Number(
      pickPerformanceField(raw, 'averageQuarterlyAppointments', 'AverageQuarterlyAppointments')
    ) || 0;
  const height = pickPerformanceField(raw, 'height', 'Height');

  return {
    name: String(pickPerformanceField(raw, 'name', 'Name') ?? ''),
    value: weekly,
    averageWeeklyAppointments: weekly,
    averageMonthlyAppointments: monthly,
    averageQuarterlyAppointments: quarterly,
    ...(height != null && String(height).trim() !== ''
      ? { height: String(height) }
      : {}),
  };
};

// ==================== CLINICAL DASHBOARD TYPES ====================

export interface ClinicalDashboardOverviewResponse {
  status: string;
  data: {
    period: {
      start_date: string;
      end_date: string;
    };
    summary: {
      total_patients: number;
      showing_improvement: number;
      require_attention: number;
      average_progress_score: number;
      assessments_due: number;
      trend: {
        percentage: number;
        is_positive: boolean;
        previous_period_count: number;
      };
    };
    progress_categories: Array<{
      category: string;
      count: number;
      percentage: number;
    }>;
    high_risk_patients: Array<{
      user_id: number;
      user_guid: string;
      first_name: string;
      last_name: string;
      date_register: string;
      birth: string | null;
      address: string | null;
      account: string | null;
      email: string | null;
      phone: string | null;
      role_id: number;
      company_id: number;
      current_risk: number;
      previous_risk: number | null;
      risk_change: number;
      improvement_status: string;
      clinical_insights: string | null;
      therapist_name: string | null;
      last_assessment_date: string;
      has_clinical_summary: boolean;
    }>;
  };
}

export interface ClinicalMentalHealthProfileResponse {
  status: string;
  data: {
    period: {
      start_date: string;
      end_date: string;
    };
    total_patients: number;
    mental_health_buckets: Array<{
      bucket: string;
      emoji: string;
      count: number;
      percentage: number;
      risk_range: string;
    }>;
  };
}

export interface ClinicalQualityImprovementResponse {
  status: string;
  data: {
    period: {
      start_date: string;
      end_date: string;
    };
    total_patients_with_improvement: number;
    quality_categories: Array<{
      stars: number;
      quality_level: string;
      count: number;
      percentage: number;
      avg_improvement: number;
      avg_days: number;
    }>;
  };
}

export interface ClinicalTherapistPerformanceResponse {
  status: string;
  data: {
    period: {
      start_date: string;
      end_date: string;
    };
    total_therapists: number;
    sort_by: string;
    therapist_performance: Array<{
      therapist_id: number;
      therapist_name: string;
      patients_reviewed: number;
      total_updates: number;
      patients_with_response: number;
      patients_showing_improvement: number;
      avg_risk_reduction: number;
      improvement_rate: number;
      response_rate: number;
    }>;
  };
}

export const dashboardApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAllCompaniesByCompanyId: build.query<TCompaniesList[], void>({
      query: () => ({
        url: `/dashboards/GetAllCompaniesList`,
        method: "GET",
      }),
    }),
    getAllUsersInCompany: build.query<TUsersForComboType[], number>({
      query: (companyId = 0) => ({
        url: `/dashboards/dashboard_getAllUsersByCompany/${
          companyId > 0 ? companyId : ""
        }`,
        method: "GET",
      }),
    }),
    getAllQuestionnairesByCompanyId: build.query<IQuestionnaireData[], void>({
      query: () => ({
        url: `/dashboards/dashboard_getAllQuestionnairesByCompany/`,
        method: "GET",
      }),
    }),
    getOnlyQuestionsWithOptionsByQuestionnaireId: build.query<
      TGetAllQuestionsAndOptionsResponse,
      number
    >({
      query: (questionnaireId) => ({
        url: `/dashboards/GetOnlyQuestionsWithOptionsByQuestionnaireId/${questionnaireId}`,
        method: "GET",
      }),
    }),

    updateQuestionsAndOptionsPreferences: build.mutation<
      TGetAllQuestionsAndOptionsResponse,
      TQuestionsOptionsPreferencesRequest
    >({
      query: (data) => ({
        url: `/dashboards/updateQuestionsAndOptionsPreferences/${data.questionnairId}/${data.companyId}`,
        method: "POST",
        body: JSON.stringify(data.body),
      }),
    }),
    getAllAnswers: build.query<TGetAlllDahboardDataResponse, { includeAnonymous?: boolean } | void>({
      query: (params) => {
        const queryParams = params && params.includeAnonymous !== undefined 
          ? { includeAnonymous: params.includeAnonymous }
          : {};
        return {
          url: `/dashboards/GetAnswers`,
          method: "GET",
          params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
        };
      },
    }),
    UpdateDiagramsType: build.mutation<
      TGetAlllDahboardDataResponse,
      { companyId: number; QuestionsPrefenceId: number; DiagramsTypeId: number }
    >({
      query: ({ QuestionsPrefenceId, DiagramsTypeId, companyId }) => ({
        url: `/dashboards/UpdateDiagramsType/${QuestionsPrefenceId}/${DiagramsTypeId}/${companyId}`,
        method: "POST",
      }),
    }),
    getTherapistJoiningData: build.query<
      TTherapistsJoinAnalysisResult,
      TTherapistsJoinAnalysisRequest
    >({
      query: (req) => ({
        url: `/dashboards/GetTherapistsJoinAnalysis/${req.companyId}/${
          req.startDate ?? ""
        }${req.endDate ? "/" + req.endDate : ""}`,
        method: "GET",
      }),
    }),
    getTherapistsWeeklyHoursAvailable: build.query<
      TTherapistsRolesAvailability[],
      number
    >({
      query: (companyId) => ({
        url: `/dashboards/GetTherapistsWeeklyHoursAvailable/${companyId}`,
        method: "GET",
      }),
    }),
    getTherapistsDashboard: build.query<
      GetTherapistsDashboardResponse,
      GetTherapistsDashboardParams
    >({
      query: ({ start_date, end_date }) => ({
        url: `/Dashboards/therapists`,
        method: "GET",
        params: {
          start_date,
          end_date,
        },
      }),
      transformResponse: (response: GetTherapistsDashboardResponse & {
        Performance?: Record<string, unknown>[];
      }) => {
        const rawList: unknown[] =
          response.performance ??
          response.Performance ??
          [];
        return {
          ...response,
          performance: rawList.map((item) =>
            normalizeTherapistPerformanceItem(item as Record<string, unknown>)
          ),
        };
      },
    }),
    getSalesDashboard: build.query<GetSalesDashboardResponse, GetSalesDashboardParams>({
      query: ({ period, start_date, end_date }) => {
        const params: Record<string, string> = {};
        if (start_date && end_date) {
          params.start_date = start_date;
          params.end_date = end_date;
        } else if (period) {
          params.period = period;
        }
        return {
          url: `/Dashboards/sales`,
          method: 'GET',
          params,
        };
      },
    }),
    getCrisisHotlineInsights: build.query<
      CrisisHotlineInsightsResponse,
      CrisisHotlineInsightsParams
    >({
      query: ({ start_date, end_date, therapistQuestionnaireIds, userQuestionnaireIds }) => {
        const params: Record<string, string | number | number[]> = {
          start_date,
          end_date,
        };

        if (therapistQuestionnaireIds && therapistQuestionnaireIds.length > 0) {
          params.therapistQuestionnaireIds = therapistQuestionnaireIds;
        }
        if (userQuestionnaireIds && userQuestionnaireIds.length > 0) {
          params.userQuestionnaireIds = userQuestionnaireIds;
        }

        return {
          url: `/dashboards/crisis-hotline-insights`,
          method: "GET",
          params,
        };
      },
    }),
    getCrisisHotlineAllData: build.query<
      CrisisHotlineInsightsResponse,
      CrisisHotlineInsightsParams
    >({
      query: ({ start_date, end_date, therapistQuestionnaireIds, userQuestionnaireIds }) => {
        const params: Record<string, string | number | number[]> = {
          start_date,
          end_date,
        };

        if (therapistQuestionnaireIds && therapistQuestionnaireIds.length > 0) {
          params.therapistQuestionnaireIds = therapistQuestionnaireIds;
        }
        if (userQuestionnaireIds && userQuestionnaireIds.length > 0) {
          params.userQuestionnaireIds = userQuestionnaireIds;
        }

        return {
          url: `/dashboards/crisis-hotline-all-data`,
          method: "GET",
          params,
        };
      },
    }),
    getCrisisHotlineQuestionnaires: build.query<
      CrisisHotlineQuestionnairesResponse,
      { questionnaireCompaniesTypeIds?: number[] } | void
    >({
      query: (arg) => {
        const params: Record<string, string> = {};
        if (arg && arg.questionnaireCompaniesTypeIds && arg.questionnaireCompaniesTypeIds.length > 0) {
          params.questionnaireCompaniesTypeIds = arg.questionnaireCompaniesTypeIds.join(",");
        }
        return {
          url: `/dashboards/crisis-hotline-questionnaires`,
          method: "GET",
          params: Object.keys(params).length > 0 ? params : undefined,
        };
      },
    }),
    getCrisisHotlineQuestions: build.query<
      CrisisHotlineQuestionsResponse,
      { questionnaireIds?: number[] }
    >({
      query: ({ questionnaireIds }) => {
        const params: Record<string, string | number | number[]> = {};
        if (questionnaireIds && questionnaireIds.length > 0) {
          params.questionnaireIds = questionnaireIds;
        }
        return {
          url: `/dashboards/crisis-hotline-questions`,
          method: "GET",
          params,
        };
      },
    }),
    getCrisisHotlineQuestionsWithOptions: build.query<
      CrisisHotlineQuestionsWithOptionsResponse,
      { questionnaireIds: number[] }
    >({
      query: ({ questionnaireIds }) => ({
        url: `/dashboards/crisis-hotline-questions-with-options`,
        method: "GET",
        params: {
          questionnaireIds: questionnaireIds.join(","),
        },
      }),
    }),
    getCrisisHotlineQuestionAnalysis: build.query<
      CrisisHotlineQuestionAnalysisResponse,
      CrisisHotlineQuestionAnalysisParams
    >({
      query: (body) => ({
        url: `/dashboards/crisis-hotline-question-analysis`,
        method: "POST",
        body,
      }),
    }),
    exportCrisisHotlinePdf: build.query<Blob, CrisisHotlineInsightsParams & { language?: string; data_scope?: string; questionnaire_companies_type_ids?: number[] }>({
      query: ({ start_date, end_date, language = "he", data_scope = "inquiries_and_reports", therapistQuestionnaireIds, userQuestionnaireIds, questionnaire_companies_type_ids }) => {
        const params: Record<string, any> = { start_date, end_date, language, data_scope };
        if (therapistQuestionnaireIds && therapistQuestionnaireIds.length > 0) {
          params.therapist_questionnaire_ids = therapistQuestionnaireIds;
        }
        if (userQuestionnaireIds && userQuestionnaireIds.length > 0) {
          params.user_questionnaire_ids = userQuestionnaireIds;
        }
        if (questionnaire_companies_type_ids && questionnaire_companies_type_ids.length > 0) {
          params.questionnaire_companies_type_ids = questionnaire_companies_type_ids;
        }
        return {
          url: `/dashboards/export-crisis-hotline-pdf`,
          method: "GET",
          params,
          responseHandler: async (response) => response.blob(),
        };
      },
      keepUnusedDataFor: 0,
    }),
    exportDailyDashboardPdf: build.query<
      Blob,
      { companyId: number; startDate?: string; endDate?: string; language?: string }
    >({
      query: ({ companyId, startDate, endDate, language = "he" }) => {
        const params: Record<string, string | number> = { companyId, language };
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        return {
          url: `/dashboards/export-daily-pdf`,
          method: "GET",
          params,
          responseHandler: async (response) => response.blob(),
        };
      },
      keepUnusedDataFor: 0,
    }),
    exportFinancialDashboardPdf: build.query<
      Blob,
      { companyId: number; startDate?: string; endDate?: string; language?: string }
    >({
      query: ({ companyId, startDate, endDate, language = "he" }) => {
        const params: Record<string, string | number> = { companyId, language };
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        return {
          url: `/dashboards/export-financial-pdf`,
          method: "GET",
          params,
          responseHandler: async (response) => response.blob(),
        };
      },
      keepUnusedDataFor: 0,
    }),
    // ==================== CLINICAL DASHBOARD ENDPOINTS ====================
    getClinicalDashboardOverview: build.query<
      ClinicalDashboardOverviewResponse,
      { start_date: string; end_date: string; therapistIds?: number[] }
    >({
      query: ({ start_date, end_date, therapistIds }) => {
        const params: Record<string, string | number | number[]> = {
          start_date,
          end_date,
        };
        if (therapistIds && therapistIds.length > 0) {
          params.therapistIds = therapistIds;
        }
        return {
          url: `/dashboards/clinical-dashboard-overview`,
          method: "GET",
          params,
        };
      },
    }),
    getClinicalMentalHealthProfile: build.query<
      ClinicalMentalHealthProfileResponse,
      { start_date: string; end_date: string }
    >({
      query: ({ start_date, end_date }) => ({
        url: `/dashboards/clinical-mental-health-profile`,
        method: "GET",
        params: { start_date, end_date },
      }),
    }),
    getClinicalQualityImprovement: build.query<
      ClinicalQualityImprovementResponse,
      { start_date: string; end_date: string }
    >({
      query: ({ start_date, end_date }) => ({
        url: `/dashboards/clinical-quality-improvement`,
        method: "GET",
        params: { start_date, end_date },
      }),
    }),
    getClinicalTherapistPerformance: build.query<
      ClinicalTherapistPerformanceResponse,
      { start_date: string; end_date: string; limit?: number; sort_by?: string }
    >({
      query: ({ start_date, end_date, limit, sort_by }) => {
        const params: Record<string, string | number> = { start_date, end_date };
        if (limit) params.limit = limit;
        if (sort_by) params.sort_by = sort_by;
        return {
          url: `/dashboards/clinical-therapist-performance`,
          method: "GET",
          params,
        };
      },
    }),
  }),
});

export const {
  useLazyGetAllCompaniesByCompanyIdQuery,
  useLazyGetAllUsersInCompanyQuery,
  useLazyGetAllQuestionnairesByCompanyIdQuery,
  useLazyGetOnlyQuestionsWithOptionsByQuestionnaireIdQuery,
  useUpdateQuestionsAndOptionsPreferencesMutation,
  useLazyGetAllAnswersQuery,
  useUpdateDiagramsTypeMutation,
  useLazyGetTherapistJoiningDataQuery,
  useLazyGetTherapistsWeeklyHoursAvailableQuery,
  useLazyGetTherapistsDashboardQuery,
  useLazyGetSalesDashboardQuery,
  useLazyGetCrisisHotlineInsightsQuery,
  useLazyGetCrisisHotlineAllDataQuery,
  useLazyGetCrisisHotlineQuestionnairesQuery,
  useLazyGetCrisisHotlineQuestionsQuery,
  useLazyGetCrisisHotlineQuestionsWithOptionsQuery,
  useLazyGetCrisisHotlineQuestionAnalysisQuery,
  useLazyExportCrisisHotlinePdfQuery,
  useLazyExportDailyDashboardPdfQuery,
  useLazyExportFinancialDashboardPdfQuery,
  useLazyGetClinicalDashboardOverviewQuery,
  useLazyGetClinicalMentalHealthProfileQuery,
  useLazyGetClinicalQualityImprovementQuery,
  useLazyGetClinicalTherapistPerformanceQuery,
} = dashboardApi;
